// Outfit APIs

import { apiCall } from './client'

/**
 * Fetches all outfits for the authenticated user
 * @param {string} token - Authentication token
 * @returns {Promise<Array<Object>>} Array of outfit objects with id, name, item_ids, created_at, updated_at
 * @throws {Error} If the API call fails
 */
export async function getOutfits(token) {
  return apiCall('/api/wardrobe/outfits', { token, errorMessage: 'Failed to fetch outfits' })
}

/**
 * Fetches a single outfit by ID
 * @param {string} token - Authentication token
 * @param {string} outfitId - ID of the outfit to fetch
 * @returns {Promise<Object>} Outfit object with id, name, item_ids, items (full wardrobe item objects)
 * @throws {Error} If the API call fails or outfit not found
 */
export async function getOutfit(token, outfitId) {
  return apiCall(`/api/wardrobe/outfits/${outfitId}`, { token, errorMessage: 'Failed to fetch outfit' })
}

/**
 * Creates a new outfit from wardrobe items
 * @param {string} token - Authentication token
 * @param {Object} outfit - Outfit data
 * @param {string} outfit.name - Name of the outfit
 * @param {Array<string>} outfit.itemIds - Array of wardrobe item IDs (max 10)
 * @returns {Promise<Object>} Created outfit object with id, name, item_ids, created_at
 * @throws {Error} If the API call fails or max items exceeded
 */
export async function createOutfit(token, { name, itemIds }) {
  return apiCall('/api/wardrobe/outfits', {
    token,
    method: 'POST',
    body: { name, item_ids: itemIds },
    errorMessage: 'Failed to create outfit'
  })
}

/**
 * Updates an existing outfit. Only provided fields will be updated.
 * @param {string} token - Authentication token
 * @param {string} outfitId - ID of the outfit to update
 * @param {Object} updates - Fields to update
 * @param {string} [updates.name] - New name for the outfit
 * @param {Array<string>} [updates.itemIds] - New array of wardrobe item IDs (replaces existing)
 * @returns {Promise<Object>} Updated outfit object
 * @throws {Error} If the API call fails or outfit not found
 */
export async function updateOutfit(token, outfitId, { name, itemIds }) {
  const body = {}
  if (name !== undefined) body.name = name
  if (itemIds !== undefined) body.item_ids = itemIds

  return apiCall(`/api/wardrobe/outfits/${outfitId}`, {
    token,
    method: 'PATCH',
    body,
    errorMessage: 'Failed to update outfit'
  })
}

/**
 * Deletes an outfit. By default soft deletes (can be restored), set permanent=true for hard delete.
 * @param {string} token - Authentication token
 * @param {string} outfitId - ID of the outfit to delete
 * @param {Object} [options] - Delete options
 * @param {boolean} [options.permanent=false] - If true, permanently deletes outfit (cannot be restored)
 * @returns {Promise<void>}
 * @throws {Error} If the API call fails or outfit not found
 */
export async function deleteOutfit(token, outfitId, { permanent = false } = {}) {
  const endpoint = permanent
    ? `/api/wardrobe/outfits/${outfitId}?permanent=true`
    : `/api/wardrobe/outfits/${outfitId}`

  return apiCall(endpoint, {
    token,
    method: 'DELETE',
    errorMessage: 'Failed to delete outfit',
    parseResponse: false
  })
}

export async function restoreOutfit(token, outfitId) {
  return apiCall(`/api/wardrobe/outfits/${outfitId}/restore`, {
    token,
    method: 'POST',
    errorMessage: 'Failed to restore outfit',
    parseResponse: false
  })
}

export async function addItemToOutfit(token, outfitId, itemId) {
  return apiCall(`/api/wardrobe/outfits/${outfitId}/items`, {
    token,
    method: 'POST',
    body: { item_id: itemId },
    errorMessage: 'Failed to add item to outfit'
  })
}

export async function removeItemFromOutfit(token, outfitId, itemId) {
  return apiCall(`/api/wardrobe/outfits/${outfitId}/items/${itemId}`, {
    token,
    method: 'DELETE',
    errorMessage: 'Failed to remove item from outfit',
    parseResponse: false
  })
}

/**
 * Uses AI to generate a creative name for an outfit based on its items
 * @param {string} token - Authentication token
 * @param {string} outfitId - ID of the outfit to generate a name for
 * @param {Array<Object>} items - Array of wardrobe item objects
 * @param {string} items[].title - Item title
 * @param {string} items[].category - Item category
 * @param {string} items[].color - Item color
 * @param {string} items[].brand - Item brand
 * @returns {Promise<Object>} Response with generated name
 * @throws {Error} If the API call fails or AI generation fails
 */
export async function generateOutfitName(token, outfitId, items) {
  return apiCall(`/api/wardrobe/outfits/${outfitId}/generate-name`, {
    token,
    method: 'POST',
    body: {
      items: items.map(item => ({
        title: item.title,
        category: item.category,
        color: item.color,
        brand: item.brand
      }))
    },
    errorMessage: 'Failed to generate outfit name'
  })
}
